import { useMemo } from 'react'
import { useWorkspaceStore } from '@/store/workspaceStore'
import { ActivityDetailView } from '../right/ActivityDetailView'
import { BackgroundDatasetView } from '../right/BackgroundDatasetView'
import { ImpactDetailView } from '../right/ImpactDetailView'
import { ChevronRight, Info, ClipboardList, Database, Calculator } from 'lucide-react'
import type { ActivityWithExchanges } from '@/lib/types/project-graph'

interface RightPanelProps {
  activities: ActivityWithExchanges[]
  projectName?: string
  projectDatabase?: string
  onExchangeCreated?: () => void
  onCollapse: () => void
}

export function RightPanel({
  activities,
  projectName,
  projectDatabase,
  onExchangeCreated,
  onCollapse,
}: RightPanelProps) {
  const { selectedNodeId, selectedNodeType, rightTab, setRightTab, bgSynthExchanges } =
    useWorkspaceStore()

  // Wrap bg synth exchanges in a virtual activity so BackgroundDatasetView's
  // existing exchange lookup (activities.flatMap(...).find(...)) resolves them.
  const activitiesWithSynth = useMemo(() => {
    const synthValues = Object.values(bgSynthExchanges)
    if (synthValues.length === 0) return activities
    const synthActivity: ActivityWithExchanges = {
      id: '__bg_synth__',
      code: '__bg_synth__',
      project_id: '',
      name: '__bg_synth__',
      unit: '',
      location: '',
      reference_product: '',
      exchanges: synthValues,
      created_at: '',
      updated_at: '',
    }
    return [synthActivity, ...activities]
  }, [activities, bgSynthExchanges])

  // Find the selected activity
  const selectedActivity =
    selectedNodeType === 'activity' && selectedNodeId
      ? activities.find((a) => a.id === selectedNodeId)
      : null

  // Find activity containing selected exchange
  const activityWithSelectedExchange =
    selectedNodeType === 'exchange' && selectedNodeId
      ? activities.find((a) => a.exchanges.some((e) => e.id === selectedNodeId))
      : null

  // Use either the directly selected activity or the parent of selected exchange
  const currentActivity = selectedActivity || activityWithSelectedExchange || null

  const hasSelection = selectedNodeId !== null

  return (
    <div className="w-full h-full bg-background border-l border-border flex flex-col">
      {/* Header with tabs - fixed height */}
      <div className="flex-shrink-0 flex items-center justify-between px-2 py-1.5 border-b border-border bg-surface-sunken/50">
        <div className="flex gap-1">
          <TabButton
            active={rightTab === 'activity'}
            onClick={() => setRightTab('activity')}
            icon={<ClipboardList className="h-3.5 w-3.5" />}
            label="Activity"
          />
          <TabButton
            active={rightTab === 'background'}
            onClick={() => setRightTab('background')}
            icon={<Database className="h-3.5 w-3.5" />}
            label="Background"
          />
          <TabButton
            active={rightTab === 'impact'}
            onClick={() => setRightTab('impact')}
            icon={<Calculator className="h-3.5 w-3.5" />}
            label="Impact"
          />
        </div>

        <button
          onClick={onCollapse}
          className="p-1 hover:bg-accent rounded-sm transition-colors shrink-0"
          title="Collapse panel"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Content area - takes remaining space with relative positioning */}
      <div className="flex-1 relative">
        {/* Absolute positioned scrollable container */}
        <div className="absolute inset-0 overflow-y-auto scrollbar-visible">
          {rightTab === 'activity' &&
            (!hasSelection ? (
              <EmptyState
                icon={Info}
                title="Select a Node"
                description="Click on an activity in the graph to view its inputs and outputs"
              />
            ) : currentActivity ? (
              <ActivityDetailView
                activity={currentActivity}
                projectDatabase={projectDatabase}
                allActivities={activities}
                onExchangeCreated={onExchangeCreated}
              />
            ) : (
              <EmptyState
                icon={Info}
                title="Activity Not Found"
                description="The selected node could not be found"
              />
            ))}

          {rightTab === 'background' && (
            <BackgroundDatasetView
              activities={activitiesWithSynth}
              projectDatabase={projectDatabase}
            />
          )}

          {rightTab === 'impact' && (
            <ImpactDetailView
              activity={currentActivity}
              projectName={projectName}
              projectDatabase={projectDatabase}
            />
          )}

        </div>
      </div>
    </div>
  )
}

// Simple tab button component
interface TabButtonProps {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}

function TabButton({ active, onClick, icon, label }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`h-7 px-2.5 text-xs gap-1.5 flex items-center rounded-md transition-colors ${
        active
          ? 'bg-background text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
      }`}
    >
      {icon}
      {label}
    </button>
  )
}

// Empty State Component
interface EmptyStateProps {
  icon: typeof Info
  title: string
  description: string
}

function EmptyState({ icon: Icon, title, description }: EmptyStateProps) {
  return (
    <div className="p-4 text-center text-muted-foreground h-full flex flex-col items-center justify-center">
      <Icon className="h-12 w-12 mb-3 opacity-20" />
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs mt-1">{description}</p>
    </div>
  )
}
